import React, { createContext, useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from './services/fbaInboundApi';

export const FBAInboundContext = createContext();

// Step definitions
export const STEPS = [
  { id: 0, key: 'plans', title: 'Select Plan', description: 'Choose existing or create new' },
  { id: 1, key: 'address', title: 'Address Assignment', description: 'Set warehouse origin' },
  { id: 2, key: 'plan', title: 'Create Plan', description: 'Generate inbound plan with items' },
  { id: 3, key: 'packing', title: 'Packing', description: 'Define box groups and contents' },
  { id: 4, key: 'placement', title: 'Placement', description: 'Select Amazon placement option' },
  { id: 5, key: 'transport', title: 'Transport', description: 'Set transport mode' },
  { id: 6, key: 'tracking', title: 'Tracking', description: 'Enter tracking details' },
  { id: 7, key: 'labels', title: 'Labels', description: 'Generate and download labels' }
];

export const FBAInboundProvider = ({ children }) => {
  const { outboundId } = useParams();
  const navigate = useNavigate();

  // ============================================
  // Workflow State
  // ============================================
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [maxAccessibleStep, setMaxAccessibleStep] = useState(0);

  // ============================================
  // Data State
  // ============================================
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState(null);

  // Outbound data
  const [outboundShipment, setOutboundShipment] = useState(null);
  const [outboundItems, setOutboundItems] = useState([]);

  // Plan data
  const [availablePlans, setAvailablePlans] = useState([]);
  const [planId, setPlanId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planItems, setPlanItems] = useState([]);

  // Packing data
  const [boxGroups, setBoxGroups] = useState([]);
  const [existingBoxContents, setExistingBoxContents] = useState([]); // Boxes from database
  const [packingOptions, setPackingOptions] = useState([]);
  const [selectedPackingOption, setSelectedPackingOption] = useState(null);

  // Placement data
  const [placementOptions, setPlacementOptions] = useState([]);
  const [selectedPlacementOption, setSelectedPlacementOption] = useState(null);

  // Transport data - initialize from localStorage if available
  const [transportMode, setTransportMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(`fba_transport_mode_${outboundId}`);
      return saved || null;
    }
    return null;
  });
  const [shipmentLocked, setShipmentLocked] = useState(false);

  // Tracking data
  const [trackingDetails, setTrackingDetails] = useState({});

  // Labels data
  const [labels, setLabels] = useState([]);
  const [labelsLoading, setLabelsLoading] = useState(false);

  // Client data
  const [clients, setClients] = useState([]);

  // Operation tracking
  const [currentOperationId, setCurrentOperationId] = useState(null);

  // ============================================
  // Step Navigation
  // ============================================

  const nextStep = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((stepId) => {
    // Block navigation to steps beyond Step 1 if no plan exists
    if (!planId && stepId > 1) {
      console.warn('Cannot navigate to step', stepId, 'without a plan');
      return false;
    }

    // Determine minimum accessible step based on plan progress
    // Once certain milestones are reached, earlier steps are locked
    let minStep = 0;
    if (planId) {
      // Once a plan exists, can't go back to address assignment (step 1)
      minStep = 2;

      // Once placement is selected, can't go back to packing
      if (selectedPlacementOption) {
        minStep = 4;
      }

      // Once shipment is locked, can't go back before transport
      if (shipmentLocked) {
        minStep = 5;
      }
    }

    // Block backward navigation to locked steps
    if (stepId < minStep && stepId !== 0) {
      console.warn('Cannot navigate back to step', stepId, '- plan progress prevents it');
      return false;
    }

    // Can go to any completed step, current step, or up to maxAccessibleStep
    const maxFromCompleted = completedSteps.size > 0 ? Math.max(...completedSteps) + 1 : 0;
    const maxAllowed = Math.max(maxFromCompleted, maxAccessibleStep);

    if (stepId >= 0 && stepId <= maxAllowed && stepId < STEPS.length) {
      setCurrentStep(stepId);
      return true;
    }
    return false;
  }, [completedSteps, maxAccessibleStep, planId, selectedPlacementOption, shipmentLocked]);

  const isStepAccessible = useCallback((stepId) => {
    if (stepId === 0) return true;

    // Determine minimum accessible step based on plan progress
    let minStep = 0;
    if (planId) {
      minStep = 2; // Can't go back to address once plan exists

      if (selectedPlacementOption) {
        minStep = 4; // Can't go back to packing once placement selected
      }

      if (shipmentLocked) {
        minStep = 5; // Can't go back before transport once locked
      }
    }

    // If step is below minimum, it's not accessible (locked)
    if (stepId < minStep && stepId !== 0) {
      return false;
    }

    // Accessible if previous step is completed OR if stepId <= maxAccessibleStep
    const maxFromCompleted = completedSteps.size > 0 ? Math.max(...completedSteps) + 1 : 0;
    const maxAllowed = Math.max(maxFromCompleted, maxAccessibleStep);
    return stepId <= maxAllowed;
  }, [completedSteps, maxAccessibleStep, planId, selectedPlacementOption, shipmentLocked]);

  const markStepCompleted = useCallback((stepId) => {
    setCompletedSteps(prev => new Set([...prev, stepId]));
  }, []);

  // ============================================
  // Data Fetching
  // ============================================

  // Step 0: Plan Management
  const loadAvailablePlans = useCallback(async () => {
    if (!outboundId) return;

    try {
      const response = await api.getPlansForOutbound(outboundId);
      const plans = response.data || response || [];
      setAvailablePlans(Array.isArray(plans) ? plans : []);
      return plans;
    } catch (err) {
      console.error('Failed to load plans:', err);
      setAvailablePlans([]);
      return [];
    }
  }, [outboundId]);

  const selectExistingPlan = useCallback(async (plan) => {
    if (!plan) return;

    setLoading(true);
    setError(null);

    try {
      // Set the plan data
      setPlanId(plan.id);
      setPlan(plan);

      // Mark step 0 as completed and move to appropriate step
      markStepCompleted(0);

      // Determine which step to go to based on plan state
      const selectedPlacement = plan.placement_options?.find(p => p.is_selected);
      const placementConfirmed = selectedPlacement?.confirmed_at || plan.is_locked;

      if (placementConfirmed) {
        // Placement is confirmed (locked) - go to tracking or labels
        setShipmentLocked(true);
        // Restore transport mode from localStorage if available
        const savedMode = localStorage.getItem(`fba_transport_mode_${outboundId}`);
        if (savedMode) {
          setTransportMode(savedMode);
        }
        setSelectedPlacementOption(selectedPlacement);
        setCurrentStep(6); // Tracking
        setMaxAccessibleStep(7);
        // Mark previous steps as completed
        markStepCompleted(1);
        markStepCompleted(2);
        markStepCompleted(3);
        markStepCompleted(4);
        markStepCompleted(5);
      } else if (selectedPlacement) {
        // Placement selected but not confirmed - go to transport
        setSelectedPlacementOption(selectedPlacement);
        setCurrentStep(5); // Transport
        setMaxAccessibleStep(5);
        markStepCompleted(1);
        markStepCompleted(2);
        markStepCompleted(3);
        markStepCompleted(4);
      } else if (plan.placement_options?.length > 0) {
        // Placement options exist - go to placement
        setCurrentStep(4); // Placement
        setMaxAccessibleStep(4);
        markStepCompleted(1);
        markStepCompleted(2);
        markStepCompleted(3);
      } else if (plan.packing_options?.some(p => p.confirmed_at)) {
        // Packing confirmed - go to placement
        setCurrentStep(4); // Placement
        setMaxAccessibleStep(4);
        markStepCompleted(1);
        markStepCompleted(2);
        markStepCompleted(3);
      } else if (plan.amazon_plan_id) {
        // Plan created - go to packing
        setCurrentStep(3); // Packing
        setMaxAccessibleStep(3);
        markStepCompleted(1);
        markStepCompleted(2);
      } else {
        // Just started - go to address
        setCurrentStep(1); // Address
        setMaxAccessibleStep(1);
      }

      // Restore additional plan data
      if (plan.placement_options?.length > 0) {
        setPlacementOptions(plan.placement_options);
      }
      if (plan.packing_options?.length > 0) {
        setPackingOptions(plan.packing_options);
        const confirmedPacking = plan.packing_options.find(p => p.confirmed_at);
        if (confirmedPacking) {
          setSelectedPackingOption(confirmedPacking);
        }
      }

    } catch (err) {
      console.error('Failed to select plan:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [markStepCompleted, outboundId]);

  const createNewPlan = useCallback(async () => {
    if (!outboundId) return;

    setLoading(true);
    setError(null);

    try {
      // Void any existing active plans first
      const activePlans = availablePlans.filter(p => !p.is_canceled);
      for (const plan of activePlans) {
        try {
          console.log(`Voiding existing plan ${plan.id}...`);
          await api.deletePlan(plan.id);
        } catch (err) {
          console.warn(`Failed to void plan ${plan.id}:`, err.message);
          // Continue anyway - the plan might already be voided
        }
      }

      // Reset all state
      setPlanId(null);
      setPlan(null);
      setPlanItems([]);
      setBoxGroups([]);
      setPackingOptions([]);
      setSelectedPackingOption(null);
      setPlacementOptions([]);
      setSelectedPlacementOption(null);
      setTransportMode(null);
      setShipmentLocked(false);
      setTrackingDetails({});
      setLabels([]);
      setCompletedSteps(new Set([0])); // Only step 0 completed

      // Clear localStorage
      if (outboundId) {
        localStorage.removeItem(`fba_transport_mode_${outboundId}`);
        localStorage.removeItem(`fba_shipment_locked_${outboundId}`);
      }

      // Move to step 1 (Address Assignment)
      setCurrentStep(1);
      setMaxAccessibleStep(1);

      // Reload plans list
      await loadAvailablePlans();

    } catch (err) {
      console.error('Failed to create new plan:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [outboundId, availablePlans, loadAvailablePlans]);

  // Helper to fetch and restore plan data
  const fetchPlanAndRestore = useCallback(async (planIdToFetch) => {
    if (!planIdToFetch) return;

    try {
      const planResponse = await api.getPlan(planIdToFetch);
      const planData = planResponse.data || planResponse;

      setPlan(planData);
      setPlanId(planData.id);

      // Restore placement options
      if (planData.placement_options?.length > 0) {
        setPlacementOptions(planData.placement_options);
        const selected = planData.placement_options.find(p => p.is_selected);
        if (selected) {
          setSelectedPlacementOption(selected);
        }
      }

      // Restore packing options
      if (planData.packing_options?.length > 0) {
        setPackingOptions(planData.packing_options);
        const confirmed = planData.packing_options.find(p => p.confirmed_at);
        if (confirmed) {
          setSelectedPackingOption(confirmed);
        }
      }

      // Restore transport mode from localStorage
      const savedMode = localStorage.getItem(`fba_transport_mode_${outboundId}`);
      if (savedMode) {
        setTransportMode(savedMode);
      }

      // Restore locked state
      const savedLocked = localStorage.getItem(`fba_shipment_locked_${outboundId}`);
      if (savedLocked === 'true') {
        setShipmentLocked(true);
      }

    } catch (err) {
      console.error('Failed to fetch plan:', err);
    }
  }, [outboundId]);

  const fetchOutboundData = useCallback(async (shipmentId) => {
    if (!shipmentId) return;

    setLoading(true);
    setError(null);

    const API_URL = import.meta.env.VITE_API_URL;

    try {
      const [shipmentResponse, itemsResponse] = await Promise.all([
        api.getOutboundShipment(shipmentId),
        api.getOutboundItems(shipmentId)
      ]);

      setOutboundShipment(shipmentResponse.shipment);
      setOutboundItems(itemsResponse.items || []);

      // Fetch existing box contents from the database
      try {
        const boxResponse = await fetch(`${API_URL}/api/get_all_box_content_plans/${shipmentId}`);
        const boxData = await boxResponse.json();

        console.log('Box data from API:', boxData); // Debug log

        if (Array.isArray(boxData) && boxData.length > 0) {
          // Group the flat data by box number
          // Handle both uppercase and lowercase field names (MySQL config dependent)
          const boxMap = {};
          boxData.forEach(item => {
            const boxNum = item.BOX_NUMBER || item.box_number;
            if (!boxMap[boxNum]) {
              boxMap[boxNum] = {
                boxNumber: boxNum,
                length: item.LENGTH || item.length || 0,
                width: item.WIDTH || item.width || 0,
                height: item.HEIGHT || item.height || 0,
                weight: item.WEIGHT || item.weight || 0,
                pallet: item.PALLET || item.pallet,
                items: []
              };
            }
            const fnsku = item.FNSKU || item.fnsku;
            const sku = item.SKU || item.sku;
            const asin = item.ASIN || item.asin;
            if (fnsku || sku || asin) {
              boxMap[boxNum].items.push({
                fnsku: fnsku,
                sku: sku,
                asin: asin,
                quantity: item.QTY || item.qty || 0,
                title: item.TITLE || item.title
              });
            }
          });
          const boxes = Object.values(boxMap).sort((a, b) => a.boxNumber - b.boxNumber);
          console.log('Parsed boxes:', boxes); // Debug log
          setExistingBoxContents(boxes);
        }
      } catch (boxErr) {
        console.error('Error fetching box contents:', boxErr);
      }

      // Check for existing plans
      const plansResponse = await api.getPlansForOutbound(shipmentId);
      console.log('Plans for outbound:', plansResponse.data?.map(p => ({
        id: p.id,
        is_canceled: p.is_canceled,
        is_locked: p.is_locked,
        is_packing_information_set: p.is_packing_information_set,
        has_placement_selected: p.placement_options?.some(po => po.is_selected),
        packing_options_confirmed: p.packing_options?.some(po => po.confirmed_at)
      })));

      if (plansResponse.data && plansResponse.data.length > 0) {
        // Find the most recent non-canceled plan (sort by created_at descending)
        const nonCanceledPlans = plansResponse.data
          .filter(p => !p.is_canceled)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        console.log('Non-canceled plans sorted by date:', nonCanceledPlans.map(p => ({
          id: p.id,
          created_at: p.created_at,
          is_packing_information_set: p.is_packing_information_set
        })));

        const activePlan = nonCanceledPlans[0]; // Get the most recent one
        if (activePlan) {
          console.log('Loading active plan:', activePlan.id, {
            is_canceled: activePlan.is_canceled,
            is_locked: activePlan.is_locked,
            is_packing_information_set: activePlan.is_packing_information_set,
            packing_option_confirmed: activePlan.packing_options?.some(po => po.confirmed_at),
            placement_selected: activePlan.placement_options?.some(po => po.is_selected),
            transport_mode: activePlan.transport_mode
          });

          // Warn if plan is in an inconsistent state
          const hasPlacement = activePlan.placement_options?.some(po => po.is_selected);
          const hasPackingConfirmed = activePlan.packing_options?.some(po => po.confirmed_at);
          if (hasPlacement && !hasPackingConfirmed) {
            console.warn('WARNING: Plan has placement selected but packing not confirmed - may be in bad state');
          }
          if (hasPlacement && !activePlan.is_packing_information_set) {
            console.warn('WARNING: Plan has placement selected but is_packing_information_set is false - may be in bad state');
          }

          setPlanId(activePlan.id);
          setPlan(activePlan);
          // Restore progress based on plan state
          restoreProgressFromPlan(activePlan);
        } else {
          console.log('All plans are canceled, none active');
          // Clear any stale state
          setPlanId(null);
          setPlan(null);
        }
      }
    } catch (err) {
      console.error('Error fetching outbound data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const data = await api.getClients();
      setClients(data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  }, []);

  // Fetch existing box contents from the database
  const fetchExistingBoxContents = useCallback(async (shipmentId) => {
    if (!shipmentId) return [];

    const API_URL = import.meta.env.VITE_API_URL;
    try {
      const response = await fetch(`${API_URL}/api/get_all_box_content_plans/${shipmentId}`);
      const data = await response.json();

      console.log('fetchExistingBoxContents - Raw data:', data); // Debug log

      if (Array.isArray(data) && data.length > 0) {
        // Group the flat data by box number
        // Handle both uppercase and lowercase field names
        const boxMap = {};
        data.forEach(item => {
          const boxNum = item.BOX_NUMBER || item.box_number;
          if (!boxMap[boxNum]) {
            boxMap[boxNum] = {
              boxNumber: boxNum,
              length: item.LENGTH || item.length || 0,
              width: item.WIDTH || item.width || 0,
              height: item.HEIGHT || item.height || 0,
              weight: item.WEIGHT || item.weight || 0,
              pallet: item.PALLET || item.pallet,
              items: []
            };
          }
          // Add item if it has data
          const fnsku = item.FNSKU || item.fnsku;
          const sku = item.SKU || item.sku;
          const asin = item.ASIN || item.asin;
          if (fnsku || sku || asin) {
            boxMap[boxNum].items.push({
              fnsku: fnsku,
              sku: sku,
              asin: asin,
              quantity: item.QTY || item.qty || 0,
              title: item.TITLE || item.title
            });
          }
        });

        // Convert to array and sort by box number
        const boxes = Object.values(boxMap).sort((a, b) => a.boxNumber - b.boxNumber);
        console.log('fetchExistingBoxContents - Parsed boxes:', boxes); // Debug log
        setExistingBoxContents(boxes);
        return boxes;
      }
      return [];
    } catch (err) {
      console.error('Error fetching existing box contents:', err);
      return [];
    }
  }, []);

  const refreshPlan = useCallback(async () => {
    if (!planId) return;

    try {
      const planData = await api.getPlan(planId);
      console.log('Full plan data:', planData.data);
      console.log('Placement options in plan:', planData.data?.placement_options);
      setPlan(planData.data);

      // Update related state from plan
      if (planData.data.packing_options) {
        setPackingOptions(planData.data.packing_options);
        const selected = planData.data.packing_options.find(p => p.confirmed_at);
        if (selected) setSelectedPackingOption(selected);
      }

      if (planData.data.placement_options) {
        setPlacementOptions(planData.data.placement_options);
        const selected = planData.data.placement_options.find(p => p.is_selected);
        if (selected) setSelectedPlacementOption(selected);
      }

      return planData.data;
    } catch (err) {
      console.error('Error refreshing plan:', err);
      throw err;
    }
  }, [planId]);

  // Restore workflow progress based on existing plan state
  const restoreProgressFromPlan = useCallback((planData) => {
    const newCompletedSteps = new Set();
    let maxAccessibleStep = 0;

    // Get selected placement for logging
    const logSelectedPlacement = planData.placement_options?.find(p => p.is_selected);
    const logShipments = logSelectedPlacement?.shipments || [];

    console.log('Restoring progress from plan:', {
      id: planData.id,
      ship_from_address: !!planData.ship_from_address,
      packing_options: planData.packing_options?.length || 0,
      is_packing_information_set: planData.is_packing_information_set,
      placement_options: planData.placement_options?.length || 0,
      has_selected_placement: !!logSelectedPlacement,
      is_locked: planData.is_locked,
      transport_mode: planData.transport_mode,
      placement_confirmed_at: logSelectedPlacement?.confirmed_at,
      shipments: logShipments.map(s => ({
        id: s.id,
        shipment_id: s.shipment_id,
        selected_transport_option: s.selected_transport_option,
        transport_mode: s.transport_mode,
        self_ship_appointment_details: !!s.self_ship_appointment_details,
        amazon_partnered_carrier: s.amazon_partnered_carrier,
        shipment_confirmation_id: s.shipment_confirmation_id,
        is_locked: s.is_locked,
        status: s.status,
        amazon_reference_id: s.amazon_reference_id
      }))
    });

    // Step 0 (Address) - complete if ship_from_address exists
    if (planData.ship_from_address || planData.ship_from_address_id) {
      newCompletedSteps.add(0);
      maxAccessibleStep = 1;
    }

    // Step 1 (Plan Created) - complete if plan exists with items
    if (planData.id && planData.items?.length > 0) {
      newCompletedSteps.add(1);
      maxAccessibleStep = 2;
    }

    // Step 2 (Packing/Box Content) - complete if packing info is set
    if (planData.is_packing_information_set === true) {
      newCompletedSteps.add(2);
      maxAccessibleStep = 3;
    } else if (planData.packing_options?.some(p => p.confirmed_at)) {
      // Packing option confirmed but info not fully set - still accessible
      maxAccessibleStep = 2;
    }

    // Step 3 (Placement Selected)
    const selectedPlacementOption = planData.placement_options?.find(p => p.is_selected);
    if (selectedPlacementOption) {
      newCompletedSteps.add(3);
      maxAccessibleStep = 4;

      // Check if transport mode has been set on any shipment
      const shipments = selectedPlacementOption.shipments || [];
      let detectedTransportMode = null;

      for (const shipment of shipments) {
        if (shipment.selected_transport_option) {
          detectedTransportMode = shipment.selected_transport_option;
          break;
        }
        if (shipment.transport_mode) {
          detectedTransportMode = shipment.transport_mode;
          break;
        }
        if (shipment.self_ship_appointment_details) {
          // Non-partnered transport
          detectedTransportMode = 'NON_PARTNERED_SMALL_PARCEL';
          break;
        }
        if (shipment.amazon_partnered_carrier) {
          detectedTransportMode = 'PARTNERED_GROUND_SMALL_PARCEL';
          break;
        }
      }

      if (detectedTransportMode) {
        console.log('Transport mode detected on shipments:', detectedTransportMode);
        setTransportMode(detectedTransportMode);
      }

      // Also check localStorage for transport mode (set before locking, before page refresh)
      if (!detectedTransportMode) {
        // Use planData.outbound_shipment_id to get the outboundId for localStorage key
        const outboundIdForStorage = planData.outbound_shipment_id || planData.outbound_id;
        if (outboundIdForStorage) {
          const savedMode = localStorage.getItem(`fba_transport_mode_${outboundIdForStorage}`);
          if (savedMode) {
            console.log('Transport mode restored from localStorage:', savedMode);
            setTransportMode(savedMode);
            detectedTransportMode = savedMode;
          }
        }
      }

      // If transport mode is set (from API or localStorage), user should go to step 4
      if (detectedTransportMode && !planData.is_locked) {
        console.log('Transport mode set but not locked - should be on step 4');
      }
    } else if (planData.placement_options?.length > 0) {
      // Placement options exist but not selected - accessible
      maxAccessibleStep = Math.max(maxAccessibleStep, 3);
    }

    // Step 4 (Transport Set & Locked)
    // Check multiple indicators for locked status
    let isLocked = planData.is_locked;

    // Also check shipment-level lock indicators
    if (!isLocked && selectedPlacementOption) {
      const shipments = selectedPlacementOption.shipments || [];
      for (const shipment of shipments) {
        // Check various indicators that a shipment is locked/confirmed
        if (shipment.shipment_confirmation_id ||
            shipment.is_locked ||
            shipment.confirmation_status === 'CONFIRMED' ||
            shipment.status === 'CONFIRMED' ||
            shipment.amazon_reference_id) {
          console.log('Shipment locked indicator found:', shipment);
          isLocked = true;
          break;
        }
      }
    }

    // Check placement option for confirmation
    if (!isLocked && selectedPlacementOption?.confirmed_at) {
      console.log('Placement option confirmed_at found:', selectedPlacementOption.confirmed_at);
      isLocked = true;
    }

    // Also check if placement is selected and has shipment confirmation ID
    if (!isLocked && selectedPlacementOption?.is_selected) {
      // Check if any shipment has a confirmation marker
      const shipments = selectedPlacementOption.shipments || [];
      for (const shipment of shipments) {
        if (shipment.shipment_confirmation_id || shipment.amazon_reference_id) {
          console.log('Shipment has confirmation ID - is locked');
          isLocked = true;
          break;
        }
      }
    }

    // Check localStorage as fallback (Amazon may have confirmed but database not yet synced)
    if (!isLocked) {
      const outboundIdForStorage = planData.outbound_shipment_id || planData.outbound_id;
      if (outboundIdForStorage) {
        const savedLocked = localStorage.getItem(`fba_shipment_locked_${outboundIdForStorage}`);
        if (savedLocked === 'true') {
          console.log('Shipment locked status restored from localStorage');
          isLocked = true;
        }
      }
    }

    if (isLocked) {
      console.log('Shipment is locked');
      newCompletedSteps.add(4);
      setShipmentLocked(true);
      maxAccessibleStep = 5;
    }

    // Also check for transport mode at plan level
    if (planData.transport_mode) {
      setTransportMode(planData.transport_mode);
    }

    // Step 5 (Tracking Entered)
    const selectedPlacement = planData.placement_options?.find(p => p.is_selected);
    if (selectedPlacement?.shipments?.some(s => s.tracking_details)) {
      newCompletedSteps.add(5);
      maxAccessibleStep = 6;
    }

    // Update packing options state
    if (planData.packing_options) {
      setPackingOptions(planData.packing_options);
      const confirmedPacking = planData.packing_options.find(p => p.confirmed_at);
      if (confirmedPacking) setSelectedPackingOption(confirmedPacking);
    }

    // Update placement options state
    if (planData.placement_options) {
      setPlacementOptions(planData.placement_options);
      const selectedPlacementOpt = planData.placement_options.find(p => p.is_selected);
      if (selectedPlacementOpt) setSelectedPlacementOption(selectedPlacementOpt);
    }

    setCompletedSteps(newCompletedSteps);
    setMaxAccessibleStep(maxAccessibleStep);

    // Set current step to the first incomplete step, but allow access up to maxAccessibleStep
    let targetStep = 0;
    for (let i = 0; i <= STEPS.length; i++) {
      if (!newCompletedSteps.has(i)) {
        targetStep = Math.min(i, maxAccessibleStep);
        break;
      }
    }
    setCurrentStep(targetStep);

    console.log('Restored progress:', {
      completedSteps: [...newCompletedSteps],
      maxAccessibleStep,
      currentStep: targetStep
    });

    return { completedSteps: newCompletedSteps, maxAccessibleStep };
  }, []);

  // ============================================
  // Workflow Actions
  // ============================================

  // Step 1: Set Ship From Address
  const setShipFromAddress = useCallback(async (addressId) => {
    if (!outboundShipment?.id) {
      throw new Error('No outbound shipment selected');
    }

    setLoading(true);
    try {
      await api.setShipFromAddress(outboundShipment.id, addressId);
      markStepCompleted(1);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [outboundShipment, markStepCompleted]);

  // Helper: Cancel all non-canceled plans for this outbound
  const cancelAllOldPlans = useCallback(async (shipmentId) => {
    try {
      const plansResponse = await api.getPlansForOutbound(shipmentId);
      const activePlans = (plansResponse.data || []).filter(p => !p.is_canceled);

      if (activePlans.length > 0) {
        console.log(`Canceling ${activePlans.length} old plans...`);
        for (const plan of activePlans) {
          try {
            await api.deletePlan(plan.id);
            console.log('Canceled plan:', plan.id);
          } catch (err) {
            console.warn('Failed to cancel plan:', plan.id, err.message);
          }
        }
      }
    } catch (err) {
      console.warn('Error checking for old plans:', err);
    }
  }, []);

  // Step 2: Create Inbound Plan
  const createPlan = useCallback(async (channelId, items) => {
    if (!outboundShipment?.id) {
      throw new Error('No outbound shipment selected');
    }

    setLoading(true);
    setStatusMessage('Canceling any old plans...');

    // Cancel all existing plans first to ensure only one active plan
    await cancelAllOldPlans(outboundShipment.id);

    setStatusMessage('Creating inbound plan...');
    try {
      const response = await api.createInboundPlan(outboundShipment.id, channelId, items);
      const newPlanId = response.data.id;
      setPlanId(newPlanId);
      setPlanItems(items);

      // Get the operation ID from create_plan_operation_id
      const operationId = response.data.create_plan_operation_id;

      if (operationId) {
        console.log('Waiting for create plan operation:', operationId);
        setStatusMessage('Waiting for Amazon to process plan...');

        // Poll operation until complete
        let attempts = 0;
        const maxAttempts = 60; // 2 minutes max

        while (attempts < maxAttempts) {
          const opStatus = await api.checkOperationStatus(operationId);
          console.log('Create plan operation status:', opStatus);

          if (opStatus.data?.status !== 'IN_PROGRESS') {
            if (opStatus.data?.status === 'ERROR') {
              throw new Error('Plan creation failed: ' + JSON.stringify(opStatus.data?.problems));
            }
            break;
          }

          setStatusMessage(`Waiting for Amazon... (${attempts * 2}s)`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        }

        if (attempts >= maxAttempts) {
          throw new Error('Plan creation timed out');
        }
      }

      // Trigger packing options generation
      console.log('Triggering packing options...');
      setStatusMessage('Generating packing options...');
      await api.triggerPackingOptions(newPlanId);

      // Now poll the plan until packing_options are populated
      console.log('Waiting for packing options to populate...');
      setStatusMessage('Waiting for packing options...');

      let planData = null;
      let retries = 0;
      const maxRetries = 15;

      while (retries < maxRetries) {
        const updatedPlan = await api.getPlan(newPlanId);
        planData = updatedPlan.data;
        console.log(`Plan refresh attempt ${retries + 1}, packing_options:`, planData?.packing_options?.length || 0);

        if (planData?.packing_options && planData.packing_options.length > 0) {
          console.log('Packing options populated:', planData.packing_options);
          break;
        }

        setStatusMessage(`Waiting for packing options... (${retries * 2}s)`);
        // Wait 2 seconds before retrying
        await new Promise(resolve => setTimeout(resolve, 2000));
        retries++;
      }

      if (!planData?.packing_options || planData.packing_options.length === 0) {
        throw new Error('Packing options not generated. Please try again.');
      }

      setPlan(planData);
      setStatusMessage('');

      markStepCompleted(2);
      return response;
    } catch (err) {
      setError(err.message);
      setStatusMessage('');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [outboundShipment, markStepCompleted, cancelAllOldPlans]);

  // Step 3: Box Group Operations
  const createBoxGroup = useCallback(async (boxGroupData) => {
    if (!planId) throw new Error('No plan created');

    try {
      const response = await api.createBoxGroup(planId, boxGroupData);
      setBoxGroups(prev => [...prev, response.data]);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [planId]);

  const submitPackingInformation = useCallback(async () => {
    if (!planId) throw new Error('No plan created');

    setLoading(true);
    try {
      const response = await api.setPackingInformation(planId);
      if (response.operation_id) {
        setCurrentOperationId(response.operation_id);
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [planId]);

  const confirmPacking = useCallback(async (packingOptionId) => {
    if (!planId) throw new Error('No plan created');

    setLoading(true);
    try {
      const response = await api.confirmPackingOption(planId, packingOptionId);
      if (response.operation_id) {
        setCurrentOperationId(response.operation_id);
      }
      markStepCompleted(3);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [planId, markStepCompleted]);

  // Step 4: Placement Operations
  const generatePlacement = useCallback(async () => {
    if (!planId) throw new Error('No plan created');

    setLoading(true);
    setStatusMessage('Checking placement status...');

    try {
      // First, refresh plan to check current state
      let planData = await refreshPlan();

      // If placement options already exist, just return them
      if (planData?.placement_options && planData.placement_options.length > 0) {
        console.log('Placement options already exist:', planData.placement_options.length);
        setStatusMessage('');
        return planData.placement_options;
      }

      // Check for any ongoing operations by querying the operations list
      setStatusMessage('Checking for ongoing operations...');
      try {
        const opsResult = await api.getAllOperations(1, 10);
        const ongoingOps = (opsResult.data || []).filter(op =>
          op.status === 'IN_PROGRESS' &&
          (op.description?.includes('generatePlacement') || op.description?.includes('Placement'))
        );

        if (ongoingOps.length > 0) {
          console.log('Found ongoing placement operation:', ongoingOps[0].id);
          setStatusMessage('Waiting for ongoing operation to complete...');

          // Wait for the ongoing operation
          let attempts = 0;
          const maxAttempts = 60;

          while (attempts < maxAttempts) {
            const opStatus = await api.checkOperationStatus(ongoingOps[0].id);
            console.log('Ongoing operation status:', opStatus);

            if (opStatus.data?.status !== 'IN_PROGRESS') {
              break;
            }

            setStatusMessage(`Waiting for operation... (${attempts * 2}s)`);
            await new Promise(resolve => setTimeout(resolve, 2000));
            attempts++;
          }

          // After operation completes, trigger and fetch placement options
          await api.triggerPlacementOptions(planId);
          planData = await refreshPlan();

          if (planData?.placement_options && planData.placement_options.length > 0) {
            setStatusMessage('');
            return planData.placement_options;
          }
        }
      } catch (opCheckErr) {
        console.warn('Could not check ongoing operations:', opCheckErr);
      }

      // No existing options and no ongoing operation - start fresh
      setStatusMessage('Generating placement options...');
      const response = await api.generatePlacementOptions(planId);

      // The response is an operation ID (string) or JSON with operation_id
      const operationId = typeof response === 'string' ? response : response?.operation_id || response?.data?.id;

      if (operationId) {
        console.log('Waiting for generate placement operation:', operationId);
        setStatusMessage('Waiting for Amazon to generate placement options...');
        // Note: We handle polling internally, don't set currentOperationId to avoid wizard overlay conflict

        // Poll operation until complete
        let attempts = 0;
        const maxAttempts = 60;

        while (attempts < maxAttempts) {
          const opStatus = await api.checkOperationStatus(operationId);
          console.log('Generate placement operation status:', opStatus);

          if (opStatus.data?.status !== 'IN_PROGRESS') {
            if (opStatus.data?.status === 'ERROR') {
              throw new Error('Placement generation failed: ' + JSON.stringify(opStatus.data?.problems));
            }
            break;
          }

          setStatusMessage(`Waiting for Amazon... (${attempts * 2}s)`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        }

        if (attempts >= maxAttempts) {
          throw new Error('Placement generation timed out');
        }
      }

      // Trigger placement options to be fetched (similar to packing options)
      console.log('Triggering placement options...');
      setStatusMessage('Fetching placement options...');
      await api.triggerPlacementOptions(planId);

      // Poll plan until placement_options are populated
      setStatusMessage('Waiting for placement options...');
      let retries = 0;
      const maxRetries = 30; // 60 seconds max

      while (retries < maxRetries) {
        planData = await refreshPlan();
        console.log(`Placement options poll attempt ${retries + 1}, options:`, planData?.placement_options?.length || 0);

        if (planData?.placement_options && planData.placement_options.length > 0) {
          console.log('Placement options populated:', planData.placement_options);
          break;
        }

        setStatusMessage(`Waiting for placement options... (${retries * 2}s)`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        retries++;
      }

      if (!planData?.placement_options || planData.placement_options.length === 0) {
        throw new Error('No placement options were generated. Please try again.');
      }

      setStatusMessage('');
      return planData.placement_options;
    } catch (err) {
      setError(err.message);
      setStatusMessage('');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [planId, refreshPlan]);

  const selectPlacement = useCallback(async (placementOptionId) => {
    if (!planId) throw new Error('No plan created');

    setLoading(true);
    try {
      console.log('Calling selectPlacementOption with:', placementOptionId);
      await api.selectPlacementOption(planId, placementOptionId);

      // Timer logic: wait 1-4 seconds then verify
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Verify selection - use placement_option_id (Amazon's ID) for matching
      const planData = await refreshPlan();
      const selected = planData.placement_options?.find(p =>
        p.placement_option_id === placementOptionId || p.id === placementOptionId
      );

      if (selected?.is_selected) {
        setSelectedPlacementOption(selected);
        markStepCompleted(4);
        return true;
      }

      throw new Error('Placement selection not confirmed');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [planId, refreshPlan, markStepCompleted]);

  // Step 5: Transport Operations
  const setTransport = useCallback(async (mode, shipmentIdOverride = null) => {
    if (!planId) throw new Error('No plan created');
    if (!selectedPlacementOption) throw new Error('No placement option selected');

    setLoading(true);
    setStatusMessage('Refreshing plan data...');

    // Refresh plan to get latest data
    let freshPlacementOption = selectedPlacementOption;
    try {
      const freshPlan = await api.getPlan(planId);
      console.log('Fresh plan data:', freshPlan.data);

      if (freshPlan.data?.placement_options) {
        // Find the selected placement option in fresh data
        const freshSelected = freshPlan.data.placement_options.find(p =>
          p.is_selected || p.id === selectedPlacementOption.id || p.placement_option_id === selectedPlacementOption.placement_option_id
        );
        if (freshSelected) {
          console.log('Found fresh placement option:', freshSelected);
          freshPlacementOption = freshSelected;
        }
      }
    } catch (refreshErr) {
      console.warn('Could not refresh plan:', refreshErr);
    }

    setStatusMessage('Setting transport mode...');

    // Get all possible IDs to try
    const internalId = freshPlacementOption.id;
    const amazonPlacementId = freshPlacementOption.placement_option_id;

    // Get shipment data from fresh placement option
    const shipments = freshPlacementOption.shipments || [];
    const shipmentIds = freshPlacementOption.shipment_ids || [];

    console.log('Selected placement option:', selectedPlacementOption);
    console.log('Shipments array:', shipments);
    console.log('Shipment IDs:', shipmentIds);

    // Get shipment ID - try multiple sources
    let shipmentId = shipmentIdOverride;
    let shipmentInternalId = null;

    if (shipments.length > 0) {
      const firstShipment = shipments[0];
      console.log('First shipment data:', firstShipment);

      // Get both the Amazon shipment ID and internal ID if available
      shipmentId = firstShipment.shipment_id || firstShipment.amazon_shipment_id || shipmentIds[0];
      shipmentInternalId = firstShipment.id;
    } else if (shipmentIds.length > 0) {
      shipmentId = shipmentIds[0];
    }

    if (!shipmentId) throw new Error('No shipment ID found. Please refresh and try again.');

    // Try different combinations of IDs - Amazon IDs first (they work!)
    const idsToTry = [
      { placementId: amazonPlacementId, shipmentId: shipmentId, desc: 'amazon placement + amazon shipment' },
      { placementId: internalId, shipmentId: shipmentId, desc: 'internal placement + amazon shipment' },
    ];

    // Add shipment internal ID combinations if available (less likely to work)
    if (shipmentInternalId) {
      idsToTry.push(
        { placementId: amazonPlacementId, shipmentId: shipmentInternalId, desc: 'amazon placement + internal shipment' },
        { placementId: internalId, shipmentId: shipmentInternalId, desc: 'internal placement + internal shipment' }
      );
    }

    let lastError = null;
    try {
      for (const combo of idsToTry) {
        console.log(`Trying ${combo.desc}:`, { planId, placementOptionId: combo.placementId, shipmentId: combo.shipmentId, mode });

        try {
          await api.setTransportModePreference(planId, combo.placementId, combo.shipmentId, mode);
          console.log(`Success with ${combo.desc}`);
          setTransportMode(mode);
          // Persist to localStorage so it survives page refresh
          if (outboundId) {
            localStorage.setItem(`fba_transport_mode_${outboundId}`, mode);
          }
          return true;
        } catch (err) {
          console.log(`Failed with ${combo.desc}:`, err.message);
          lastError = err;
        }
      }

      // All combinations failed
      setError(lastError?.message || 'Failed to set transport mode');
      throw lastError;
    } finally {
      setLoading(false);
      setStatusMessage('');
    }
  }, [planId, selectedPlacementOption, outboundId]);

  // Helper function to complete the transportation confirmation flow
  // This is needed after placement is confirmed to actually lock the shipment for labels
  const completeTransportationConfirmation = async (amazonPlacementId, shipmentId) => {
    console.log('Starting transportation confirmation flow...');

    // Step 1: Generate transportation options
    setStatusMessage('Generating transportation options...');
    const genResult = await api.generateTransportationOptions(
      planId,
      amazonPlacementId,
      [{ shipment_id: shipmentId, start_date: new Date().toISOString() }]
    );

    const genOperationId = typeof genResult === 'string' ? genResult : genResult?.operation_id;
    if (genOperationId) {
      console.log('Waiting for generateTransportationOptions operation:', genOperationId);
      let attempts = 0;
      while (attempts < 30) {
        const opStatus = await api.checkOperationStatus(genOperationId);
        if (opStatus.data?.status !== 'IN_PROGRESS') {
          if (opStatus.data?.status === 'ERROR') {
            console.warn('Generate transportation options warning:', opStatus.data?.problems);
            // Don't throw - warnings are OK
          }
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
      }
    }

    // Step 2: Fetch transportation options into database
    setStatusMessage('Fetching transportation options...');
    await api.fetchTransportationOptions(planId, amazonPlacementId, shipmentId);

    // Step 3: Get the plan to find the transportation option
    const planWithTransport = await api.getPlan(planId);
    const selectedPlacement = planWithTransport.data?.placement_options?.find(p => p.is_selected);
    const shipment = selectedPlacement?.shipments?.find(s => s.shipment_id === shipmentId);
    const transportOptions = shipment?.transportation_options || [];

    console.log('Transportation options found:', transportOptions.length);

    if (transportOptions.length === 0) {
      throw new Error('No transportation options available. Please try again.');
    }

    // Use the first transportation option (usually UPS for partnered SPD)
    const transportOption = transportOptions[0];
    console.log('Using transportation option:', transportOption);

    // Step 4: Assign boxes to shipment
    setStatusMessage('Assigning boxes to shipment...');
    await api.setBoxesIds(planId);

    // Step 5: Confirm transportation option
    setStatusMessage('Confirming transportation option...');
    const confirmResult = await api.confirmTransportationOption(
      planId,
      amazonPlacementId,
      [{ shipment_id: shipmentId, transportation_option_id: transportOption.transportation_option_id }]
    );

    const confirmOpId = typeof confirmResult === 'string' ? confirmResult : confirmResult?.operation_id;
    if (confirmOpId) {
      console.log('Waiting for confirmTransportationOption operation:', confirmOpId);
      let attempts = 0;
      while (attempts < 30) {
        const opStatus = await api.checkOperationStatus(confirmOpId);
        if (opStatus.data?.status !== 'IN_PROGRESS') {
          if (opStatus.data?.status === 'ERROR') {
            throw new Error('Transportation confirmation failed: ' + JSON.stringify(opStatus.data?.problems));
          }
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;
      }
    }

    // Step 6: Mark transportation option as confirmed in YYZ database
    setStatusMessage('Finalizing shipment confirmation...');
    await api.markTransportationOptionAsConfirmed(
      planId,
      [{ shipment_id: shipmentId, transportation_option_id: transportOption.transportation_option_id }]
    );

    console.log('Transportation confirmation completed successfully!');
    return true;
  };

  const lockShipment = useCallback(async () => {
    if (!planId) throw new Error('No plan created');
    if (!selectedPlacementOption) throw new Error('No placement option selected');

    // Get both ID formats - Amazon IDs work!
    const internalId = selectedPlacementOption.id;
    const amazonPlacementId = selectedPlacementOption.placement_option_id;

    // Get shipment ID
    const shipments = selectedPlacementOption.shipments || [];
    const shipmentIds = selectedPlacementOption.shipment_ids || [];
    let shipmentId = null;
    if (shipments.length > 0) {
      shipmentId = shipments[0].shipment_id || shipments[0].amazon_shipment_id;
    }
    if (!shipmentId && shipmentIds.length > 0) {
      shipmentId = shipmentIds[0];
    }

    console.log('Locking shipment - amazon ID:', amazonPlacementId, 'internal ID:', internalId, 'shipment ID:', shipmentId);

    setLoading(true);
    setStatusMessage('Checking packing status...');

    // First, refresh plan to check packing status
    try {
      const freshPlan = await api.getPlan(planId);

      // Check if plan is canceled
      if (freshPlan.data?.is_canceled) {
        setLoading(false);
        setStatusMessage('');
        const cancelError = new Error('PLAN_CANCELED: This plan has been canceled by Amazon. Please go back to Step 3 and use the "Auto-Recover Plan" feature to create a new plan.');
        cancelError.isPlanCanceled = true;
        setError(cancelError.message.replace('PLAN_CANCELED: ', ''));
        throw cancelError;
      }

      const packingOptions = freshPlan.data?.packing_options || [];
      const confirmedPackingOption = packingOptions.find(p => p.confirmed_at);

      console.log('Fresh plan data for lock check:', {
        is_packing_information_set: freshPlan.data?.is_packing_information_set,
        packing_options: packingOptions.length,
        has_confirmed_packing: !!confirmedPackingOption,
        is_canceled: freshPlan.data?.is_canceled
      });

      // Step 1: If no packing option is confirmed, confirm one first
      if (!confirmedPackingOption && packingOptions.length > 0) {
        console.log('No packing option confirmed, confirming first one...');
        setStatusMessage('Confirming packing option...');

        try {
          // Use the first packing option's ID
          const packingOptionId = packingOptions[0].packing_option_id || packingOptions[0].id;
          console.log('Confirming packing option:', packingOptionId);

          const confirmResult = await api.confirmPackingOption(planId, packingOptionId);
          console.log('Packing option confirmation result:', confirmResult);

          // If we got an operation ID, wait for it
          const operationId = typeof confirmResult === 'string' ? confirmResult : confirmResult?.operation_id;
          if (operationId) {
            setStatusMessage('Waiting for packing option confirmation...');
            let attempts = 0;
            while (attempts < 30) {
              const opStatus = await api.checkOperationStatus(operationId);
              if (opStatus.data?.status !== 'IN_PROGRESS') {
                if (opStatus.data?.status === 'ERROR') {
                  console.warn('Packing option confirmation failed:', opStatus.data?.problems);
                }
                break;
              }
              await new Promise(resolve => setTimeout(resolve, 2000));
              attempts++;
            }
          }

          // Wait a moment for the state to update
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (confirmErr) {
          console.warn('Could not auto-confirm packing option:', confirmErr);
          // Check if this is a canceled plan error
          if (confirmErr.message?.includes('cancelled inbound plan') || confirmErr.message?.includes('canceled inbound plan')) {
            setLoading(false);
            setStatusMessage('');
            const cancelError = new Error('This plan has been canceled by Amazon. Please go back to Step 3 and use the "Auto-Recover Plan" feature to create a new plan.');
            cancelError.isPlanCanceled = true;
            setError(cancelError.message);
            throw cancelError;
          }
        }
      }

      // Refresh plan after confirming packing option
      const updatedPlan = await api.getPlan(planId);

      // Step 2: If packing information is not set, submit it
      if (!updatedPlan.data?.is_packing_information_set) {
        console.log('Packing information not set, attempting to submit...');
        setStatusMessage('Submitting packing information...');

        try {
          const packingResult = await api.setPackingInformation(planId);
          console.log('Packing information submission result:', packingResult);

          // If we got an operation ID, wait for it
          const operationId = typeof packingResult === 'string' ? packingResult : packingResult?.operation_id;
          if (operationId) {
            setStatusMessage('Waiting for packing information...');
            let attempts = 0;
            while (attempts < 30) {
              const opStatus = await api.checkOperationStatus(operationId);
              if (opStatus.data?.status !== 'IN_PROGRESS') {
                if (opStatus.data?.status === 'ERROR') {
                  throw new Error('Packing submission failed: ' + JSON.stringify(opStatus.data?.problems));
                }
                break;
              }
              await new Promise(resolve => setTimeout(resolve, 2000));
              attempts++;
            }
          }

          // Wait a moment for the state to update
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (packingErr) {
          console.warn('Could not auto-submit packing:', packingErr);
          // Continue anyway - the lock might still work
        }
      }
    } catch (checkErr) {
      console.warn('Could not check plan status:', checkErr);
    }

    setStatusMessage('Locking shipment...');

    // Helper function to poll confirm operation and verify lock
    const pollConfirmOperationAndVerify = async (operationResult) => {
      // Check if we got an operation ID
      const operationId = typeof operationResult === 'string'
        ? operationResult
        : operationResult?.operation_id;

      if (operationId) {
        console.log('Polling confirm placement operation:', operationId);
        setStatusMessage('Waiting for Amazon to confirm placement...');

        // Poll operation until complete
        let attempts = 0;
        const maxAttempts = 60; // 2 minutes max

        while (attempts < maxAttempts) {
          try {
            const opStatus = await api.checkOperationStatus(operationId);
            console.log('Confirm placement operation status:', opStatus);

            if (opStatus.data?.status !== 'IN_PROGRESS') {
              if (opStatus.data?.status === 'ERROR') {
                const problems = opStatus.data?.problems;
                throw new Error('Placement confirmation failed: ' + JSON.stringify(problems));
              }
              console.log('Confirm placement operation completed with status:', opStatus.data?.status);
              break;
            }
          } catch (pollErr) {
            console.warn('Error polling operation status:', pollErr);
            // Continue polling - might be a transient error
          }

          setStatusMessage(`Waiting for Amazon... (${attempts * 2}s)`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        }

        if (attempts >= maxAttempts) {
          throw new Error('Placement confirmation timed out - please try again');
        }
      }

      // After operation completes, verify the placement is actually confirmed
      setStatusMessage('Verifying placement confirmation...');
      await new Promise(resolve => setTimeout(resolve, 1000)); // Brief delay for API state update

      const verifiedPlan = await api.getPlan(planId);
      console.log('Verified plan data:', {
        is_locked: verifiedPlan.data?.is_locked,
        placement_options: verifiedPlan.data?.placement_options?.map(p => ({
          id: p.id,
          is_selected: p.is_selected,
          confirmed_at: p.confirmed_at
        }))
      });

      // Check if placement is confirmed
      const selectedPlacement = verifiedPlan.data?.placement_options?.find(p => p.is_selected);
      if (selectedPlacement?.confirmed_at || verifiedPlan.data?.is_locked) {
        console.log('Placement confirmed successfully!');
        return true;
      }

      // Check shipment-level confirmation
      const shipments = selectedPlacement?.shipments || [];
      for (const ship of shipments) {
        if (ship.shipment_confirmation_id || ship.amazon_reference_id || ship.is_locked) {
          console.log('Shipment confirmed:', ship);
          return true;
        }
      }

      console.warn('Placement confirmation not verified in plan data - may still be processing');
      return true; // Assume success if operation completed without error
    };

    // Try with Amazon ID first (this is what works)
    try {
      const result = await api.confirmPlacementOption(planId, amazonPlacementId, shipmentId);
      await pollConfirmOperationAndVerify(result);

      // Sync placement confirmation to YYZ database before proceeding
      try {
        setStatusMessage('Syncing placement confirmation...');
        await api.markPlacementOptionAsConfirmed(planId, amazonPlacementId);
      } catch (syncErr) {
        console.log('markPlacementOptionAsConfirmed info:', syncErr.message);
        // Continue anyway - might already be marked
      }

      // Now complete the transportation confirmation flow
      await completeTransportationConfirmation(amazonPlacementId, shipmentId);

      setShipmentLocked(true);
      markStepCompleted(5);
      // Store locked state - keep transport mode for restore on refresh
      if (outboundId) {
        localStorage.setItem(`fba_shipment_locked_${outboundId}`, 'true');
      }
      setStatusMessage('');
      return true;
    } catch (err) {
      console.log('confirmPlacementOption error:', err.message);

      // Check if error says "already confirmed" - this means placement is confirmed
      // but we still need to complete the transportation confirmation steps
      if (err.message?.includes('already confirmed')) {
        console.log('Amazon says placement is already confirmed - proceeding with transportation confirmation');

        // Sync placement confirmation to YYZ database
        try {
          setStatusMessage('Syncing placement confirmation...');
          await api.markPlacementOptionAsConfirmed(planId, amazonPlacementId);
        } catch (syncErr) {
          console.log('markPlacementOptionAsConfirmed info:', syncErr.message);
        }

        // Now complete the transportation confirmation flow
        try {
          await completeTransportationConfirmation(amazonPlacementId, shipmentId);

          setShipmentLocked(true);
          markStepCompleted(5);

          if (outboundId) {
            localStorage.setItem(`fba_shipment_locked_${outboundId}`, 'true');
          }

          setStatusMessage('');
          return true;
        } catch (transportErr) {
          console.error('Transportation confirmation failed:', transportErr);
          setError(transportErr.message);
          setStatusMessage('');
          throw transportErr;
        }
      }

      console.log('Failed with Amazon ID, trying internal ID...', err.message);
      try {
        const result = await api.confirmPlacementOption(planId, internalId, shipmentId);
        await pollConfirmOperationAndVerify(result);

        // Sync placement confirmation to YYZ database before proceeding
        try {
          setStatusMessage('Syncing placement confirmation...');
          await api.markPlacementOptionAsConfirmed(planId, amazonPlacementId);
        } catch (syncErr) {
          console.log('markPlacementOptionAsConfirmed info:', syncErr.message);
          // Continue anyway - might already be marked
        }

        // Complete transportation confirmation flow
        await completeTransportationConfirmation(amazonPlacementId, shipmentId);

        setShipmentLocked(true);
        markStepCompleted(5);
        if (outboundId) {
          localStorage.setItem(`fba_shipment_locked_${outboundId}`, 'true');
        }
        setStatusMessage('');
        return true;
      } catch (err2) {
        console.log('confirmPlacementOption error (internal ID):', err2.message);

        // Check if error says "already confirmed"
        if (err2.message?.includes('already confirmed')) {
          console.log('Amazon says placement is already confirmed - proceeding with transportation confirmation');

          try {
            setStatusMessage('Syncing placement confirmation...');
            await api.markPlacementOptionAsConfirmed(planId, amazonPlacementId);
          } catch (syncErr) {
            console.log('markPlacementOptionAsConfirmed info:', syncErr.message);
          }

          try {
            await completeTransportationConfirmation(amazonPlacementId, shipmentId);

            setShipmentLocked(true);
            markStepCompleted(5);
            if (outboundId) {
              localStorage.setItem(`fba_shipment_locked_${outboundId}`, 'true');
            }
            setStatusMessage('');
            return true;
          } catch (transportErr) {
            console.error('Transportation confirmation failed:', transportErr);
            setError(transportErr.message);
            setStatusMessage('');
            throw transportErr;
          }
        }

        // Parse the error to give a better message
        let errorMessage = err2.message;
        try {
          const parsed = JSON.parse(errorMessage);
          if (parsed.message) {
            errorMessage = parsed.message;
            if (errorMessage.includes('Packing information')) {
              errorMessage = 'Packing information was not submitted. Please go back to Step 3 (Packing) and ensure the packing is properly confirmed.';
            }
          }
        } catch (parseErr) {
          // Keep original message
        }
        setError(errorMessage);
        setStatusMessage('');
        throw new Error(errorMessage);
      }
    } finally {
      setLoading(false);
      setStatusMessage('');
    }
  }, [planId, selectedPlacementOption, markStepCompleted, outboundId]);

  // Step 6: Tracking Operations
  const updateTracking = useCallback(async (trackingData) => {
    if (!planId) throw new Error('No plan created');

    setLoading(true);
    try {
      if (transportMode === api.TRANSPORT_MODES.NON_PARTNERED_LTL) {
        await api.updateTrackingLTL(planId, trackingData);
      } else {
        await api.updateTrackingSPD(planId, trackingData);
      }
      setTrackingDetails(trackingData);
      markStepCompleted(6);
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [planId, transportMode, markStepCompleted]);

  // Step 7: Label Operations
  const requestLabels = useCallback(async (shipmentIdOverride = null) => {
    if (!planId) throw new Error('No plan created');

    // Get shipment ID from selected placement option or use override
    let shipmentId = shipmentIdOverride;
    if (!shipmentId && selectedPlacementOption) {
      const shipments = selectedPlacementOption.shipments || [];
      if (shipments.length > 0) {
        shipmentId = shipments[0].shipment_id || shipments[0].amazon_shipment_id;
      }
      if (!shipmentId && selectedPlacementOption.shipment_ids?.length > 0) {
        shipmentId = selectedPlacementOption.shipment_ids[0];
      }
    }

    if (!shipmentId) {
      throw new Error('No shipment ID found. Please ensure a placement option is selected.');
    }

    console.log('Requesting labels for shipment:', shipmentId);

    setLabelsLoading(true);
    try {
      const response = await api.requestBoxLabels(planId, shipmentId);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLabelsLoading(false);
    }
  }, [planId, selectedPlacementOption]);

  const fetchLabels = useCallback(async (shipmentIdOverride = null) => {
    if (!planId) throw new Error('No plan created');

    // Get shipment ID from override or selected placement option
    let shipmentId = shipmentIdOverride;
    if (!shipmentId && selectedPlacementOption) {
      const shipments = selectedPlacementOption.shipments || [];
      if (shipments.length > 0) {
        shipmentId = shipments[0].shipment_id || shipments[0].amazon_shipment_id;
      }
    }

    try {
      const response = await api.getLabels(planId, shipmentId);
      setLabels(response.data || []);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [planId, selectedPlacementOption]);

  // ============================================
  // Rollback & Reset
  // ============================================

  const rollbackPlan = useCallback(async () => {
    if (!planId) return;

    setLoading(true);
    try {
      await api.deletePlan(planId);
      resetWorkflow();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [planId]);

  const resetWorkflow = useCallback(() => {
    setCurrentStep(0);
    setCompletedSteps(new Set());
    setMaxAccessibleStep(0);  // Reset step accessibility
    setPlanId(null);
    setPlan(null);
    setPlanItems([]);
    setBoxGroups([]);
    setPackingOptions([]);
    setSelectedPackingOption(null);
    setPlacementOptions([]);
    setSelectedPlacementOption(null);
    setTransportMode(null);
    setShipmentLocked(false);
    setTrackingDetails({});
    setLabels([]);
    setCurrentOperationId(null);
    setError(null);
    // Clear localStorage for transport mode and locked status
    if (outboundId) {
      localStorage.removeItem(`fba_transport_mode_${outboundId}`);
      localStorage.removeItem(`fba_shipment_locked_${outboundId}`);
    }
  }, [outboundId]);

  // Reset workflow state to Step 0 after voiding a plan
  // Used when plans are voided from Step0_PlanSelector
  const resetToStep0 = useCallback(() => {
    setCurrentStep(0);
    setCompletedSteps(new Set());
    setMaxAccessibleStep(0);
    // Clear plan data since it was voided
    setPlanId(null);
    setPlan(null);
    setBoxGroups([]);
    setPackingOptions([]);
    setSelectedPackingOption(null);
    setPlacementOptions([]);
    setSelectedPlacementOption(null);
    setTransportMode(null);
    setShipmentLocked(false);
    setTrackingDetails({});
    setLabels([]);
    setCurrentOperationId(null);
    setError(null);
    // Clear localStorage
    if (outboundId) {
      localStorage.removeItem(`fba_transport_mode_${outboundId}`);
      localStorage.removeItem(`fba_shipment_locked_${outboundId}`);
    }
  }, [outboundId]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ============================================
  // Effects
  // ============================================

  // Load outbound data when ID changes
  useEffect(() => {
    if (outboundId) {
      fetchOutboundData(outboundId);
    }
  }, [outboundId, fetchOutboundData]);

  // Load clients on mount
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // ============================================
  // Context Value
  // ============================================

  const value = {
    // Step state
    currentStep,
    completedSteps,
    maxAccessibleStep,
    steps: STEPS,

    // Navigation
    nextStep,
    prevStep,
    goToStep,
    isStepAccessible,
    markStepCompleted,

    // Loading & Error
    loading,
    statusMessage,
    error,
    clearError,

    // Outbound data
    outboundId,
    outboundShipment,
    outboundItems,
    fetchOutboundData,

    // Plan data
    availablePlans,
    loadAvailablePlans,
    selectExistingPlan,
    createNewPlan,
    planId,
    setPlanId,
    plan,
    planItems,
    refreshPlan,

    // Packing
    boxGroups,
    setBoxGroups,
    existingBoxContents,
    packingOptions,
    selectedPackingOption,

    // Placement
    placementOptions,
    selectedPlacementOption,

    // Transport
    transportMode,
    shipmentLocked,

    // Tracking
    trackingDetails,

    // Labels
    labels,
    labelsLoading,

    // Clients
    clients,

    // Operations
    currentOperationId,
    setCurrentOperationId,

    // Actions
    setShipFromAddress,
    createPlan,
    createBoxGroup,
    submitPackingInformation,
    confirmPacking,
    generatePlacement,
    selectPlacement,
    setTransport,
    lockShipment,
    updateTracking,
    requestLabels,
    fetchLabels,

    // Rollback
    rollbackPlan,
    resetWorkflow,
    resetToStep0,
    cancelAllOldPlans,

    // Navigate helper
    navigate
  };

  return (
    <FBAInboundContext.Provider value={value}>
      {children}
    </FBAInboundContext.Provider>
  );
};

export default FBAInboundContext;
